<template>
    <div class="lf-wrap">
        <!-- Logo -->
        <div class="lf-logo-wrap">
            <logo class="lf-logo" />
        </div>

        <!-- Formulario principal -->
        <template v-if="!unlock_user_form">
            <div class="lf-header">
                <h2 class="lf-title">Iniciar sesión</h2>
                <p class="lf-subtitle">Ingresa con tu usuario para continuar.</p>
            </div>

            <b-message type="is-danger" v-if="message" class="lf-error">
                <b>Error</b>: {{ message }}
            </b-message>

            <form @submit.prevent="handleLogin" class="lf-form">
                <div class="lf-field">
                    <label class="lf-label">Usuario</label>
                    <b-input
                        v-model="user.username"
                        name="usuario"
                        placeholder="Ingresa tu usuario o RUT"
                        autocomplete="off"
                        required
                    ></b-input>
                </div>

                <div class="lf-field">
                    <label class="lf-label">Contraseña</label>
                    <b-input
                        v-model="user.password"
                        name="contraseña"
                        type="password"
                        password-reveal
                        placeholder="Ingresa tu contraseña"
                        required
                    ></b-input>
                </div>

                <a href="/forgot-password" class="lf-forgot">¿Olvidó su contraseña?</a>

                <button
                    class="lf-btn-submit"
                    :disabled="loading"
                    type="submit"
                >
                    <span v-if="!loading">Iniciar sesión</span>
                    <span v-else class="lf-spinner"></span>
                </button>
            </form>
        </template>

        <!-- Primer ingreso: cambio de contraseña -->
        <template v-else>
            <div class="lf-header">
                <h2 class="lf-title">Primer ingreso</h2>
                <p class="lf-subtitle">Establece tu contraseña para continuar.</p>
            </div>

            <b-message type="is-danger" v-if="message" class="lf-error">
                <b>Error</b>: {{ message }}
            </b-message>

            <form @submit.prevent="changePassword" class="lf-form">
                <div class="lf-field">
                    <label class="lf-label">Email</label>
                    <b-input
                        name="email"
                        v-model="forms.change_password.email"
                        placeholder="tucorreo@empresa.com"
                        required
                    ></b-input>
                </div>

                <div class="lf-field">
                    <label class="lf-label">Nueva contraseña</label>
                    <b-input
                        v-model="forms.change_password.new_password"
                        name="contraseña"
                        type="password"
                        password-reveal
                        placeholder="Mínimo 8 caracteres"
                        required
                    ></b-input>
                </div>

                <div class="lf-field">
                    <label class="lf-label">Confirmar contraseña</label>
                    <b-input
                        v-model="forms.change_password.new_password_confirmation"
                        name="confirmar_contraseña"
                        type="password"
                        password-reveal
                        placeholder="Repite tu contraseña"
                        required
                    ></b-input>
                </div>

                <button
                    class="lf-btn-submit"
                    :disabled="loading"
                    type="submit"
                >
                    <span v-if="!loading">Guardar contraseña</span>
                    <span v-else class="lf-spinner"></span>
                </button>
            </form>
        </template>

        <!-- Powered by -->
        <div class="lf-powered">
            <img :src="require('@/assets/svg/powered-unifrutti.svg')" alt="Powered By Unifrutti">
        </div>

        <!-- Modals -->
        <modal-seleccionar-perfil />
    </div>
</template>

<script>
import User from '@/models/User';
import Message from '@/utils/Message';

// Components
import Logo from '@/components/shared/Logo';
import Perfiles from '@/components/Login/Perfiles';
import ModalSeleccionarPerfil from '@/components/shared/Modals/ModalSeleccionarPerfil.vue';

// Config
import { RECAPTCHA_PUBLIC_KEY } from '@/config';

export default {
    components: {
        Perfiles,
        Logo,
        ModalSeleccionarPerfil,
    },
    data() {
        return {
            user: new User('', ''),
            loading: false,
            message: '',
            unlock_user_form: false,
            tmpToken: null,
            forms: {
                change_password: {
                    email: '',
                    new_password: '',
                    new_password_confirmation: '',
                },
            },
        };
    },
    computed: {
        loggedIn() {
            return this.$store.state.auth.status.loggedIn;
        },
    },
    created() {
        if (this.loggedIn) {
            location.href = '/';
        }
    },
    mounted() {
        let recaptchaScript = document.createElement('script');
        recaptchaScript.setAttribute(
            'src',
            `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_PUBLIC_KEY}`
        );
        document.head.appendChild(recaptchaScript);
    },
    methods: {
        async handleLogin() {
            this.loading = true;
            try {
                const token = await window.grecaptcha.execute(
                    RECAPTCHA_PUBLIC_KEY,
                    { action: 'submit' }
                );

                if (this.user.username && this.user.password) {
                    this.user.recaptcha_token = token;

                    const result = await this.$store.dispatch(
                        'auth/login',
                        this.user
                    );
                    this.tmpToken = result?.token ?? null;

                    if (!result.temporal) {
                        await this.$store.dispatch('me/setPerfiles', []);

                        if (!result.access) {
                            if (result.perfiles.length > 1) {
                                await this.$store.dispatch(
                                    'me/setPerfiles',
                                    result.perfiles
                                );
                                await this.$store.dispatch(
                                    'ui/setisModalSeleccionarPerfil',
                                    true
                                );
                                return;
                            } else if (result.perfiles.length === 0) {
                                await this.$router.push('/nueva-cuenta');
                            }
                        }
                        location.href = '/';
                    } else {
                        this.unlock_user_form = true;
                    }
                }
            } catch (err) {
                Message.showErrorMessage(this, err.message, err?.errors);
            } finally {
                this.loading = false;
            }
        },
        async changePassword() {
            try {
                this.loading = true;

                const res = await this.$store.dispatch('auth/firstLogin', {
                    ...this.forms.change_password,
                    temporalToken: this.tmpToken,
                });

                Message.showSuccessMessage(this, res.message);
                setTimeout(() => {
                    window.location.replace('/login');
                }, 2000);
            } catch (err) {
                Message.showErrorMessage(this, err.message, err?.errors);
            } finally {
                this.loading = false;
            }
        },
        goRight() {
            this.$emit('go-side', 'right');
        },
    },
};
</script>

<style scoped>
/* ── Wrapper ── */
.lf-wrap {
    width: 100%;
    padding: clamp(28px, 5vh, 60px) clamp(28px, 5vw, 82px);
    display: flex;
    flex-direction: column;
    gap: clamp(16px, 2vh, 24px);
    font-family: 'Poppins', 'Segoe UI', sans-serif;
}

/* ── Logo ── */
.lf-logo-wrap {
    display: flex;
    justify-content: center;
}

.lf-logo {
    width: clamp(136px, 12vw, 184px);
    height: auto;
    display: block;
}

/* ── Header ── */
.lf-header {
    text-align: center;
}

.lf-title {
    font-size: clamp(1.7rem, 2.2vw, 2rem);
    font-weight: 700;
    color: #18212f;
    margin: 0 0 6px;
    line-height: 1.2;
}

.lf-subtitle {
    font-size: 15px;
    font-weight: 400;
    color: #69778a;
    margin: 0;
    line-height: 1.5;
}

/* ── Error message ── */
.lf-error {
    margin: 0;
}

/* ── Form ── */
.lf-form {
    display: flex;
    flex-direction: column;
    gap: clamp(14px, 1.7vh, 20px);
}

.lf-field {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.lf-label {
    font-size: 15px;
    font-weight: 600;
    color: #18212f;
    display: block;
    margin: 0;
}

/* Inputs de Buefy */
::v-deep .lf-field .control .input {
    height: 52px;
    border-radius: 12px;
    border: 1px solid #d7deea;
    padding: 0 18px;
    font-family: 'Poppins', 'Segoe UI', sans-serif;
    font-size: 1rem;
    color: #18212f;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
    box-shadow: none;
}

::v-deep .lf-field .control .input::placeholder {
    color: #99a5b5;
    font-weight: 400;
}

::v-deep .lf-field .control .input:hover {
    border-color: #184289;
    box-shadow: none;
}

::v-deep .lf-field .control .input:focus {
    border-color: #2d67b4;
    box-shadow: 0 0 0 3px rgba(45, 103, 180, 0.14);
}

/* Espacio para el ícono del reveal de contraseña */
::v-deep .lf-field .control.has-icons-right .input {
    padding-right: 48px;
}

/* ── Olvidó contraseña ── */
.lf-forgot {
    font-size: 14px;
    font-weight: 500;
    color: #18212f;
    text-decoration: none;
    align-self: flex-start;
    margin-top: -4px;
}

.lf-forgot:hover {
    color: #184289;
    text-decoration: underline;
}

/* ── Botón submit ── */
.lf-btn-submit {
    width: 100%;
    height: 52px;
    background: #184289;
    border: 1px solid #184289;
    border-radius: 12px;
    color: #ffffff;
    font-family: 'Poppins', 'Segoe UI', sans-serif;
    font-size: 16px;
    font-weight: 700;
    cursor: pointer;
    transition: background-color 0.2s ease, border-color 0.2s ease, transform 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;
}

.lf-btn-submit:hover:not(:disabled) {
    background: #12336c;
    border-color: #12336c;
}

.lf-btn-submit:active:not(:disabled) {
    transform: translateY(1px);
}

.lf-btn-submit:disabled {
    opacity: 0.72;
    cursor: not-allowed;
}

/* Spinner de carga */
.lf-spinner {
    display: inline-block;
    width: 20px;
    height: 20px;
    border: 3px solid rgba(255, 255, 255, 0.4);
    border-top-color: #ffffff;
    border-radius: 50%;
    animation: lf-spin 0.8s linear infinite;
}

@keyframes lf-spin {
    to { transform: rotate(360deg); }
}

/* ── Powered by ── */
.lf-powered {
    display: flex;
    justify-content: center;
    margin-top: auto;
}

.lf-powered img {
    width: clamp(112px, 10vw, 132px);
    height: auto;
    opacity: 0.86;
}

/* ── Responsive baja resolución ── */
@media (max-height: 760px) {
    .lf-wrap {
        gap: 12px;
    }

    ::v-deep .lf-field .control .input {
        height: 48px;
    }

    .lf-btn-submit {
        height: 48px;
    }
}
</style>
